import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Clock, 
  DollarSign, 
  Users, 
  Calendar, 
  Plus, 
  Edit3, 
  Trash2,
  Download,
  Filter,
  Search,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff
} from 'lucide-react';
import { 
  TimeEntry, 
  OnboardingProject, 
  OnboardingTask 
} from './types';

interface TimesheetPanelProps {
  projectId?: string;
  userRole: 'client' | 'vendor' | 'admin';
  onExportToBilling?: (entries: TimeEntry[]) => void;
}

export const TimesheetPanel: React.FC<TimesheetPanelProps> = ({
  projectId,
  userRole,
  onExportToBilling
}) => {
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [projects, setProjects] = useState<OnboardingProject[]>([]);
  const [tasks, setTasks] = useState<OnboardingTask[]>([]);
  const [isAddingEntry, setIsAddingEntry] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimeEntry | null>(null);
  const [filters, setFilters] = useState({
    dateRange: 'this-week',
    project: '',
    user: '',
    status: '',
    billable: 'all'
  });
  const [searchQuery, setSearchQuery] = useState('');

  // Mock data - replace with API calls
  useEffect(() => {
    const mockTimeEntries: TimeEntry[] = [
      {
        id: 'time-001',
        taskId: 'task-001',
        projectId: 'proj-001',
        userId: 'Mike Johnson',
        date: '2024-01-20',
        hours: 8,
        description: 'Stakeholder requirements gathering and documentation',
        billable: true,
        rateProfile: 'senior-developer',
        clientVisible: true,
        status: 'approved'
      },
      {
        id: 'time-002',
        taskId: 'task-001',
        projectId: 'proj-001',
        userId: 'Mike Johnson',
        date: '2024-01-21',
        hours: 4,
        description: 'Requirements review and stakeholder feedback incorporation',
        billable: true,
        rateProfile: 'senior-developer',
        clientVisible: true,
        status: 'submitted'
      },
      {
        id: 'time-003',
        taskId: 'task-002',
        projectId: 'proj-001',
        userId: 'David Rodriguez',
        date: '2024-01-20',
        hours: 6,
        description: 'Development environment setup and configuration',
        billable: true,
        rateProfile: 'mid-developer',
        clientVisible: false,
        status: 'approved'
      }
    ];

    const mockProjects: OnboardingProject[] = [
      {
        id: 'proj-001',
        name: 'Enterprise CRM Implementation',
        type: 'implementation',
        status: 'active',
        progress: 65,
        startDate: '2024-01-15',
        targetDate: '2024-04-30',
        clientName: 'TechCorp Solutions',
        projectManager: 'Sarah Chen',
        teamMembers: ['Mike Johnson', 'Lisa Wang', 'David Rodriguez', 'Emma Thompson'],
        priority: 'high',
        healthScore: 85,
        lastActivity: '2024-01-20T14:30:00Z'
      }
    ];

    const mockTasks: OnboardingTask[] = [
      {
        id: 'task-001',
        projectId: 'proj-001',
        title: 'Complete stakeholder requirements gathering',
        description: 'Conduct interviews with key stakeholders to finalize CRM requirements',
        type: 'documentation',
        status: 'in-progress',
        assignee: 'Mike Johnson',
        dueDate: '2024-01-25',
        priority: 'high',
        dependencies: [],
        estimatedHours: 16,
        actualHours: 12,
        completionPercentage: 75,
        tags: ['requirements', 'stakeholders', 'discovery'],
        attachments: [],
        comments: []
      }
    ];

    setTimeEntries(mockTimeEntries);
    setProjects(mockProjects);
    setTasks(mockTasks);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'text-green-500 bg-green-50';
      case 'submitted': return 'text-blue-500 bg-blue-50';
      case 'draft': return 'text-gray-500 bg-gray-50';
      case 'rejected': return 'text-red-500 bg-red-50';
      default: return 'text-gray-500 bg-gray-50';
    }
  };

  const getRateProfileColor = (rateProfile: string) => {
    switch (rateProfile) {
      case 'senior-developer': return 'text-purple-600 bg-purple-50';
      case 'mid-developer': return 'text-blue-600 bg-blue-50';
      case 'junior-developer': return 'text-green-600 bg-green-50';
      case 'project-manager': return 'text-orange-600 bg-orange-50';
      default: return 'text-gray-600 bg-gray-50';
    }
  };

  const filteredEntries = timeEntries.filter(entry => {
    if (filters.project && entry.projectId !== filters.project) return false;
    if (filters.user && entry.userId !== filters.user) return false;
    if (filters.status && entry.status !== filters.status) return false;
    if (filters.billable !== 'all') {
      const billableBool = filters.billable === 'true';
      if (entry.billable !== billableBool) return false;
    }
    if (searchQuery && !entry.description.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const totalHours = filteredEntries.reduce((sum, entry) => sum + entry.hours, 0);
  const billableHours = filteredEntries.filter(entry => entry.billable).reduce((sum, entry) => sum + entry.hours, 0);
  const totalBillableAmount = billableHours * 150; // Mock rate calculation

  const handleAddEntry = () => {
    setIsAddingEntry(true);
  };

  const handleEditEntry = (entry: TimeEntry) => {
    setEditingEntry(entry);
  };

  const handleDeleteEntry = (entryId: string) => {
    setTimeEntries(prev => prev.filter(entry => entry.id !== entryId));
  };

  const handleExportToBilling = () => {
    if (onExportToBilling) {
      onExportToBilling(filteredEntries);
    } else {
      console.log('Exporting to billing:', filteredEntries);
      // TODO: Implement export logic
    }
  };

  const renderTimeEntryForm = () => (
    <div className="tyler-card p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {editingEntry ? 'Edit Time Entry' : 'Add Time Entry'}
        </h3>
        <button
          onClick={() => {
            setIsAddingEntry(false);
            setEditingEntry(null);
          }}
          className="text-gray-400 hover:text-gray-600"
        >
          <XCircle className="w-5 h-5" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Project</label>
          <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#4267A1] focus:border-[#4267A1]">
            {projects.map(project => (
              <option key={project.id} value={project.id}>{project.name}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Task</label>
          <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#4267A1] focus:border-[#4267A1]">
            {tasks.map(task => (
              <option key={task.id} value={task.id}>{task.title}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
          <input
            type="date"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#4267A1] focus:border-[#4267A1]"
            defaultValue={new Date().toISOString().split('T')[0]}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Hours</label>
          <input
            type="number"
            min="0.25"
            step="0.25"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#4267A1] focus:border-[#4267A1]"
            placeholder="8.0"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Rate Profile</label>
          <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#4267A1] focus:border-[#4267A1]">
            <option value="senior-developer">Senior Developer</option>
            <option value="mid-developer">Mid Developer</option>
            <option value="junior-developer">Junior Developer</option>
            <option value="project-manager">Project Manager</option>
          </select>
        </div>

        <div className="flex items-center gap-4">
          <label className="flex items-center">
            <input type="checkbox" className="mr-2" defaultChecked />
            <span className="text-sm text-gray-700">Billable</span>
          </label>
          <label className="flex items-center">
            <input type="checkbox" className="mr-2" defaultChecked />
            <span className="text-sm text-gray-700">Client Visible</span>
          </label>
        </div>
      </div>

      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
 <textarea
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-[#4267A1] focus:border-[#4267A1]"
          rows={3}
          placeholder="Describe the work performed..."
        />
      </div>

      <div className="mt-6 flex justify-end gap-3">
        <button
          onClick={() => {
            setIsAddingEntry(false);
            setEditingEntry(null);
          }}
          className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
        >
          Cancel
        </button>
        <button className="px-4 py-2 bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] text-white rounded-md hover:shadow-lg transition-all">
          {editingEntry ? 'Update Entry' : 'Add Entry'}
        </button>
      </div>
    </div>
  );

  const renderFilters = () => (
    <div className="tyler-card p-4 mb-6">
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700">Filters:</span>
        </div>

        <select
          value={filters.dateRange}
          onChange={(e) => setFilters(prev => ({ ...prev, dateRange: e.target.value }))}
          className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:ring-[#4267A1] focus:border-[#4267A1]"
        >
          <option value="this-week">This Week</option>
          <option value="last-week">Last Week</option>
          <option value="this-month">This Month</option>
          <option value="last-month">Last Month</option>
          <option value="custom">Custom Range</option>
        </select>

        <select
          value={filters.project}
          onChange={(e) => setFilters(prev => ({ ...prev, project: e.target.value }))}
          className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:ring-[#4267A1] focus:border-[#4267A1]"
        >
          <option value="">All Projects</option>
          {projects.map(project => (
            <option key={project.id} value={project.id}>{project.name}</option>
          ))}
        </select>

        <select
          value={filters.status}
          onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
          className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:ring-[#4267A1] focus:border-[#4267A1]"
        >
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="submitted">Submitted</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>

        <select
          value={filters.billable}
          onChange={(e) => setFilters(prev => ({ ...prev, billable: e.target.value }))}
          className="px-3 py-1 text-sm border border-gray-300 rounded-md focus:ring-[#4267A1] focus:border-[#4267A1]"
        >
          <option value="all">All Entries</option>
          <option value="true">Billable Only</option>
          <option value="false">Non-Billable Only</option>
        </select>
      </div>
    </div>
  );

  const renderTimeEntries = () => (
    <div className="space-y-4">
      {filteredEntries.map(entry => (
        <motion.div
          key={entry.id}
          whileHover={{ scale: 1.01 }}
          className="tyler-card p-4"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h4 className="font-medium text-gray-900">
                  {tasks.find(t => t.id === entry.taskId)?.title || 'Unknown Task'}
                </h4>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(entry.status)}`}>
                  {entry.status}
                </span>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRateProfileColor(entry.rateProfile)}`}>
                  {entry.rateProfile.replace('-', ' ')}
                </span>
              </div>
              
              <p className="text-sm text-gray-600 mb-2">{entry.description}</p>
              
              <div className="flex items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  {entry.userId}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {entry.date}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {entry.hours}h
                </span>
                {entry.billable && (
                  <span className="flex items-center gap-1 text-green-600">
                    <DollarSign className="w-4 h-4" />
                    ${(entry.hours * 150).toFixed(2)}
                  </span>
                )}
                {entry.clientVisible ? (
                  <Eye className="w-4 h-4 text-blue-500" title="Client Visible" />
                ) : (
                  <EyeOff className="w-4 h-4 text-gray-400" title="Internal Only" />
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 ml-4">
              <button
                onClick={() => handleEditEntry(entry)}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <Edit3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleDeleteEntry(entry.id)}
                className="p-2 text-gray-400 hover:text-red-600 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );

  return (
    <div className="h-full bg-white">
      <div className="p-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] bg-clip-text text-transparent mb-4 drop-shadow-sm">
            Timesheet & Resource Planning
          </h2>
          <p className="text-gray-600 text-lg drop-shadow-sm">
            Track time, manage billable hours, and optimize resource utilization
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="tyler-card p-6 text-center"
          >
            <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mx-auto mb-3">
              <Clock className="w-6 h-6 text-blue-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900 drop-shadow-sm">{totalHours}</p>
            <p className="text-sm text-gray-600">Total Hours</p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            className="tyler-card p-6 text-center"
          >
            <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mx-auto mb-3">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900 drop-shadow-sm">{billableHours}</p>
            <p className="text-sm text-gray-600">Billable Hours</p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            className="tyler-card p-6 text-center"
          >
            <div className="flex items-center justify-center w-12 h-12 bg-purple-100 rounded-full mx-auto mb-3">
              <TrendingUp className="w-6 h-6 text-purple-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900 drop-shadow-sm">${totalBillableAmount.toFixed(0)}</p>
            <p className="text-sm text-gray-600">Billable Amount</p>
          </motion.div>

          <motion.div
            whileHover={{ scale: 1.02 }}
            className="tyler-card p-6 text-center"
          >
            <div className="flex items-center justify-center w-12 h-12 bg-orange-100 rounded-full mx-auto mb-3">
              <Users className="w-6 h-6 text-orange-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900 drop-shadow-sm">{filteredEntries.length}</p>
            <p className="text-sm text-gray-600">Time Entries</p>
          </motion.div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={handleAddEntry}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#4267A1] to-[#5A7BC4] text-white rounded-lg hover:shadow-lg transition-all"
            >
              <Plus className="w-4 h-4" />
              Add Time Entry
            </button>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleExportToBilling}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#8A9150] to-[#A5B366] text-white rounded-lg hover:shadow-lg transition-all"
            >
              <Download className="w-4 h-4" />
              Export to Billing
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search time entries..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-[#4267A1] focus:border-[#4267A1]"
            />
          </div>
        </div>

        {/* Filters */}
        {renderFilters()}

        {/* Time Entry Form */}
        {(isAddingEntry || editingEntry) && renderTimeEntryForm()}

        {/* Time Entries */}
        {renderTimeEntries()}

        {filteredEntries.length === 0 && (
          <div className="text-center py-12">
            <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No time entries found matching your criteria.</p>
          </div>
        )}
      </div>
    </div>
  );
};
